import { Coupon } from '@models/membership/Coupon'
import { Currency } from '@models/Currency'
import { MembershipProfile } from '@models/membership/MembershipProfile'
import { Plan } from '@models/membership/Plan'
import { SBModel } from '@supplement-bacon/react-toolkit/models'

/**
 * How the renewal will be paid. `missing`: card subscription with no stored
 * payment method at Adyen (only checked within the next 30 days). `null` /
 * `unknown`: not known, rendered `--`.
 */
type MembershipRenewalPaymentMethod =
  | 'card'
  | 'direct_debit'
  | 'bank_transfer'
  | 'missing'
  | 'unknown'

/** What happened (or will happen) to the renewal due on that date. */
type MembershipRenewalOutcome =
  | 'renewed'
  | 'payment_failed'
  | 'cancelled'
  | 'awaiting_transfer'
  | 'upcoming'

/** Lite, denormalized member embed - not the full `MembershipProfile` model. */
interface MembershipIncomingRenewalMember {
  id: number
  firstName: string
  lastName: string
  email: string
  avatar: string | null
}

/** Lite, denormalized plan embed - not the full `Plan` model. */
interface MembershipIncomingRenewalPlan {
  id: number
  name: string
  /**
   * Currency the row's amounts are expressed in - the plan's program currency,
   * which the endpoint denormalizes onto the plan. Only the ISO code and the
   * symbol are contractual, so the embed stays a `Pick`, not a `Currency`.
   * `null` when the program has none on file: the row then falls back to the
   * default euro rendering.
   */
  currency: Pick<Currency, 'isoCode' | 'symbol'> | null
}

/**
 * The plan as this endpoint serializes it: the shared `Plan` resource plus the
 * lite `currency` embed it adds. Not on the `Plan` model itself - only these
 * analytics rows carry it, and widening `Plan` would imply every plan payload
 * has it.
 */
type MembershipIncomingRenewalPayloadPlan = Plan & {
  currency?: Pick<Currency, 'isoCode' | 'symbol'> | null
}

/**
 * What `membership/subscriptions/incoming-renewals` actually returns: the shared
 * `Subscription` resource restricted to active subscriptions, with the plan,
 * coupon and members eager-loaded. `nextPaymentAmount` is the one field the
 * resource computes - the coupon rules are not derivable from the payload.
 */
interface MembershipIncomingRenewalPayload extends SBModel {
  renewalDate: string | null
  nextPaymentAmount: number | null
  paymentMethod?: MembershipRenewalPaymentMethod | null
  outcome?: MembershipRenewalOutcome | null
  plan?: MembershipIncomingRenewalPayloadPlan
  coupon?: Coupon
  membershipProfiles?: MembershipProfile[]
}

/**
 * The flat row both renewal tables read, mapped from the payload by
 * `mapSubscriptionToIncomingRenewal` - an upcoming renewal of an ACTIVE
 * subscription, not a transaction, so it carries no status. `member`/`plan` stay
 * lite shapes, hence `LiteMemberAvatar` (which maps the embed onto
 * `MembershipProfile.Avatar`) and `LitePlanTag` rather than `Plan.Tag`'s store
 * lookup.
 */
interface MembershipIncomingRenewal extends SBModel {
  date: string | null
  member: MembershipIncomingRenewalMember | null
  plan: MembershipIncomingRenewalPlan | null
  /**
   * Full plan price BEFORE the coupon, in cents. `null` when the payload has no
   * plan price: rendered as `--`, never as a free renewal.
   */
  amount: number | null
  /**
   * The whole coupon, not just its code: rendered with `Coupon.Avatar` like the
   * members table, which shows the discount amount and keeps the code in its
   * tooltip.
   */
  coupon: Coupon | null
  /** The NEXT payment amount of the subscription, in cents. `null` if unknown. */
  netAmount: number | null
  paymentMethod: MembershipRenewalPaymentMethod | null
  outcome: MembershipRenewalOutcome | null
}

/** Flattens the shared subscription payload into the row the tables render. */
const mapSubscriptionToIncomingRenewal = (
  subscription: MembershipIncomingRenewalPayload
): MembershipIncomingRenewal => {
  const member = subscription.membershipProfiles?.[0]
  const plan = subscription.plan

  return {
    id: subscription.id,
    date: subscription.renewalDate,
    member: member
      ? {
          id: member.id,
          firstName: member.firstName ?? '',
          lastName: member.lastName ?? '',
          email: member.email ?? '',
          avatar: member.profilePictureUrl ?? null,
        }
      : null,
    // `en.name`, like `Plan.Tag` - plan names are localized, never flat.
    plan: plan
      ? { id: plan.id, name: plan.en.name, currency: plan.currency ?? null }
      : null,
    amount: plan?.price ?? null,
    coupon: subscription.coupon ?? null,
    netAmount: subscription.nextPaymentAmount ?? null,
    paymentMethod: subscription.paymentMethod ?? null,
    outcome: subscription.outcome ?? null,
  }
}

export { mapSubscriptionToIncomingRenewal }
export type {
  MembershipIncomingRenewal,
  MembershipIncomingRenewalMember,
  MembershipIncomingRenewalPayload,
  MembershipIncomingRenewalPlan,
  MembershipRenewalOutcome,
  MembershipRenewalPaymentMethod,
}
